import React, { FormEvent, useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { updateRecord, getRecord, UpdateRecordParams, createRecordNote, toastSuccess } from '../../../api'
import { Record, RecordStatus } from '../../../models'
import { Loading } from '../../common'

export const RefundRecordForm = () => {
    const { recordId } = useParams<{ recordId: string }>()
    const navigate = useNavigate()

    const [record, setRecord] = useState<Record | null>(null)
    const [inputs, setInputs] = useState<UpdateRecordParams>({
        refund_support_link: '',
    })
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!recordId) return
        getRecord(recordId)
            .then((res) => {
                const fetched = new Record(res.data)
                setRecord(fetched)
                setInputs({
                    refund_support_link: fetched.refundSupportLink || '',
                })
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [recordId])

    const updateInputs = (field: keyof UpdateRecordParams, value: string | number) => {
        setInputs({
            ...inputs,
            [field]: value,
        })
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!recordId) {
            toast.error('Missing record ID.')
            return
        }

        try {
            await updateRecord(recordId, {
                refund_support_link: inputs.refund_support_link,
                status: RecordStatus.REFUND,
            })

            const note = `Processed refund for Record #${recordId} amounting to $${record?.paidCt}. Support Document: ${inputs.refund_support_link}`

            await createRecordNote({
                recordId: Number(recordId),
                note,
            })

            toastSuccess(`Record #${recordId} refunded successfully.`)
            navigate(`/records/refund/view/${recordId}`)
        } catch (error: any) {
            console.error('Error refunding record:', error)
        }
    }

    if (loading) {
        return <Loading />
    }

    if (!record) {
        return <p>Record not found.</p>
    }

    return (
        <div className="container card col-xs-12 col-xl-6 mt-5">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="card-title mb-0">Refund Record #{recordId}</h3>
                </div>

                <form onSubmit={handleSubmit}>
                    <table className="table">
                        <tbody>
                            <tr>
                                <th>Record ID</th>
                                <td>{record.id}</td>
                            </tr>
                            <tr>
                                <th>Status</th>
                                <td>{record.status.name}</td>
                            </tr>
                            <tr>
                                <th>Agency</th>
                                <td>
                                    <Link
                                        to={`/agencies/${record.agency.id}`}
                                        className="text-nowrap"
                                    >
                                        {record.agency.name}
                                    </Link>
                                </td>
                            </tr>
                            <tr>
                                <th>First Name</th>
                                <td>{record.guestFirstName}</td>
                            </tr>
                            <tr>
                                <th>Last Name</th>
                                <td>{record.guestLastName}</td>
                            </tr>
                            <tr>
                                <th>Confirmation Number</th>
                                <td>{record.confirmationNumber}</td>
                            </tr>
                            <tr>
                                <th>Refund Support Link</th>
                                <td>
                                    <input
                                        type="url"
                                        className="form-control"
                                        id="refundSupportLinkInput"
                                        placeholder="Enter Google Drive link"
                                        value={inputs.refund_support_link || ''}
                                        onChange={(e) =>
                                            updateInputs('refund_support_link', e.target.value)
                                        }
                                        required
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="row mb-5">
                        <div className="col-12 d-flex justify-content-center">
                            <button className="btn btn-primary" type="submit">
                                Submit
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
